import React, {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import QueriesError from "common/Empty/QueriesError";
import pRetry from "p-retry";
import {
  IS_WHOP,
  STORAGE_IS_WHOP_KEY,
  STORAGE_WHOP_EXPERIENCE_ID_KEY,
  WHOP_EXPERIENCE_ID,
} from "common/constants";
import { refreshAndPersistToken } from "@shared/services/baseQuery";
import { selectShowWhopAuthErrorPage } from "@shared/slices/common.slice";
import { clearStoreTokens } from "@shared/utils/misc";
import { useThemeContext, WhopApp } from "@whop/react/components";
import { getWhopAppId } from "app/shared/utils.ts";

const WhopProvider = ({ children }: PropsWithChildren) => {
  const whopAppId = getWhopAppId();
  if (!IS_WHOP || !whopAppId) return children;

  return (
    <WhopApp sdkOptions={{ appId: whopAppId }}>
      <InnerWhopProvider>{children}</InnerWhopProvider>
    </WhopApp>
  );
};

export default WhopProvider;

const MAX_RETRIES = 4;

const InnerWhopProvider = ({ children }: PropsWithChildren) => {
  const token = useSelector(sharedLib.selectors.authentication.selectToken);
  const showWhopErrorPage = useSelector(selectShowWhopAuthErrorPage);
  const [isError, setIsError] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [themeInitialized, setThemeInitialized] = useState(false);
  const startedRef = useRef(false);
  const whopTheme = useThemeContext();
  const dispatch = useDispatch();
  const isDarkMode = whopTheme.appearance === "dark";

  const onThemeChange = (isDarkMode: boolean) => {
    dispatch(sharedLib.slices.common.actions.setIsDarkMode(isDarkMode));
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (themeInitialized) {
      onThemeChange(isDarkMode);
    } else {
      timeout = setTimeout(() => {
        dispatch(sharedLib.slices.common.actions.setIsDarkMode(isDarkMode));
        setThemeInitialized(true);
      }, 1000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [isDarkMode]);

  useEffect(() => {
    localStorage?.setItem(STORAGE_IS_WHOP_KEY, "1");
    if (WHOP_EXPERIENCE_ID) {
      localStorage?.setItem(STORAGE_WHOP_EXPERIENCE_ID_KEY, WHOP_EXPERIENCE_ID);
    }
    clearStoreTokens();
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (token) {
      setIsError(false);
      startedRef.current = false;
      try {
        sharedLib.store.dispatch(
          sharedLib.slices.common.actions.setShowWhopAuthErrorPage(false)
        );
      } catch {}
      return;
    }

    if (showWhopErrorPage || startedRef.current) return;

    startedRef.current = true;
    let cancelled = false;
    const run = async () => {
      setIsError(false);
      try {
        await pRetry(
          async () => {
            const success = await refreshAndPersistToken(true, IS_WHOP);
            if (!success) {
              throw new Error("refresh failed");
            }
          },
          {
            retries: MAX_RETRIES,
            factor: 2,
            minTimeout: 500,
          }
        );
        if (!cancelled) {
          setIsError(false);
        }
      } catch (e) {
        if (!cancelled) {
          setIsError(true);
        }
      }
    };

    run().catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [token, showWhopErrorPage]);

  return useMemo(() => {
    if (initialized && token) return children;
    if (initialized && (isError || showWhopErrorPage)) {
      return <QueriesError isGeneralPageError={true} />;
    }
    return <LoadingSpinnerOverlay isLoading={true} spinnerSize="large" />;
  }, [children, initialized, isError, token, showWhopErrorPage]);
};
